import { useEffect, useState, type ReactNode } from 'react';
import { retrieveLaunchParams } from '@telegram-apps/sdk-react';
import * as Sentry from '@sentry/react';
import { getMe, resetSessionExpiredCount } from '../lib/api';
import { brand, fontFamily } from '../theme/tokens';
import { DKLogo } from './design/DKLogo';

type GateState =
  | { phase: 'checking' }
  | { phase: 'allowed' }
  | { phase: 'forbidden'; detail: string }
  | { phase: 'error'; detail: string };

/**
 * Authentication gate. On mount, calls /api/me to verify:
 *   1. Worker is reachable (network OK).
 *   2. initData is valid (signed by the bot whose token the Worker has).
 *   3. User is whitelisted.
 *
 * Render strategy:
 *   - checking → splash with logo + spinner
 *   - allowed → render children (the full app)
 *   - forbidden → "Access restricted" wall (whitelist mismatch)
 *   - error → "Couldn't connect" wall with retry
 *
 * This is a separate layer from NotInTelegram (which gates on `?dev=1` /
 * URL signals) — AppGate runs AFTER we've decided we're in Telegram.
 */
export function AppGate({ children }: { children: ReactNode }) {
  // ?dev=1 — UI preview mode in browser. mock-env supplies fake initData
  // whose hash can't match the real bot token, so /api/me would always 401.
  // Skip the gate entirely; render the UI for design/QA purposes.
  const isDevPreview =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('dev') === '1';

  const [state, setState] = useState<GateState>(
    isDevPreview ? { phase: 'allowed' } : { phase: 'checking' },
  );

  useEffect(() => {
    if (isDevPreview) return;
    let cancelled = false;
    setState({ phase: 'checking' });

    getMe()
      .then((user) => {
        if (cancelled) return;
        // Successful /api/me confirms a fresh, valid session — clear the
        // persisted session-expired counter so the next stale event (if any)
        // is treated as the user's first attempt, not a recurring loop.
        resetSessionExpiredCount();

        // Attach user to Sentry scope: any error captured later in this
        // session will surface "Affected users" in Sentry UI and be
        // filterable by tg_id. We deliberately send only the numeric id
        // + username (Telegram @handle, public-ish) — first_name /
        // last_name / photo_url stay out. sendDefaultPii=false in
        // sentry.ts is the second guard.
        Sentry.setUser({
          id: String(user.tg_id),
          username: user.username ?? undefined,
        });
        Sentry.setTag('is_premium', String(user.is_premium ?? false));
        // Telegram platform from launch params (ios / tdesktop / android
        // / macos / web / weba). Most TMA bugs are platform-specific —
        // this is the single most useful filter dimension. Best-effort:
        // retrieveLaunchParams may throw in dev preview where the SDK
        // isn't fully initialized; the tag is just nice-to-have.
        try {
          const lp = retrieveLaunchParams();
          if (lp.tgWebAppPlatform) {
            Sentry.setTag('tg_platform', lp.tgWebAppPlatform);
          }
        } catch {
          /* dev preview / pre-init — skip */
        }

        setState({ phase: 'allowed' });
      })
      .catch((e) => {
        if (cancelled) return;
        const msg = e instanceof Error ? e.message : String(e);
        if (msg.includes('403') || msg.toLowerCase().includes('whitelist')) {
          setState({ phase: 'forbidden', detail: msg });
        } else {
          setState({ phase: 'error', detail: msg });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isDevPreview]);

  if (state.phase === 'allowed') return <>{children}</>;

  if (state.phase === 'checking') {
    return (
      <Wall>
        <DKLogo size={80} />
        <CSSSpinner />
      </Wall>
    );
  }

  if (state.phase === 'forbidden') {
    return (
      <Wall>
        <Lock />
        <Title>Access restricted</Title>
        <Body>
          This environment is invite-only. If you believe you should have access, contact the admin.
        </Body>
      </Wall>
    );
  }

  // error
  return (
    <Wall>
      <Title>Couldn't connect</Title>
      <Body>{state.detail}</Body>
      <RetryButton onClick={() => location.reload()} />
    </Wall>
  );
}

// ─────────────────────────────────────────────────────────────────────
function Wall({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '32px 24px',
        textAlign: 'center',
        gap: 16,
        fontFamily,
        color: 'var(--tgui--text_color)',
        background: 'var(--tgui--bg_color)',
      }}
    >
      {children}
    </div>
  );
}

function Title({ children }: { children: ReactNode }) {
  return (
    <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, letterSpacing: -0.4 }}>
      {children}
    </h1>
  );
}

function Body({ children }: { children: ReactNode }) {
  return (
    <p style={{ margin: 0, fontSize: 14, color: 'var(--tgui--hint_color)', maxWidth: 320, lineHeight: 1.5 }}>
      {children}
    </p>
  );
}

/**
 * Standalone CSS spinner — telegram-ui's Spinner needs <AppRoot> context,
 * which we don't have at the gate layer (it lives inside Layout, which is
 * inside AppGate's children). Inline keyframes keep this self-contained.
 */
function CSSSpinner() {
  return (
    <>
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          border: `3px solid ${brand.solid}33`,
          borderTopColor: brand.solid,
          animation: 'dk-gate-spin 0.8s linear infinite',
        }}
      />
      <style>{`
        @keyframes dk-gate-spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
}

function Lock() {
  return (
    <div
      style={{
        width: 80,
        height: 80,
        borderRadius: '50%',
        background: `${brand.solid}22`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 8,
      }}
    >
      <svg width={40} height={40} viewBox="0 0 24 24" fill="none" stroke={brand.solid} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
        <rect x="4" y="11" width="16" height="10" rx="2" />
        <path d="M8 11V7a4 4 0 0 1 8 0v4" />
      </svg>
    </div>
  );
}

function RetryButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        marginTop: 8,
        padding: '12px 28px',
        border: 'none',
        background: brand.gradient,
        color: '#fff',
        borderRadius: 14,
        fontSize: 15,
        fontWeight: 600,
        cursor: 'pointer',
        boxShadow: brand.glow,
        fontFamily,
      }}
    >
      Try again
    </button>
  );
}
